/*
 * email-popup.js — Dansu guestlist email-capture popup behaviour.
 *
 * - Fires on the first scroll past ~40% of the viewport height, or after 15s,
 *   whichever comes first (only once).
 * - Suppressed via cookie + localStorage after dismiss OR successful submit
 *   (so we don't re-nag).
 * - On submit:
 *     1. POSTs {email, source, product_handle, product_title, locale} to the
 *        Supabase `newsletter-subscribe` edge fn (CRM subscribe + SWEAT11
 *        logging + branded, product-tailored welcome email). The product
 *        fields come off the root element's data attributes — empty when the
 *        popup isn't rendered on a product page.
 *     2. Submits the NATIVE Shopify customer form in the background (hidden
 *        iframe target) so the email also lands in the Shopify customer DB,
 *        marketing-subscribed, tagged "newsletter".
 * - Shows the success state with the returned discount code + a copy button.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "dansu_email_popup_done";
  var COOKIE_NAME = "dansu_email_popup_done";
  var SHOW_DELAY_MS = 15000;
  var SCROLL_TRIGGER_RATIO = 0.4;
  var SUPPRESS_DAYS = 60;

  var root = document.getElementById("dansu-email-popup");
  if (!root) return;

  // ── suppression helpers ──────────────────────────────────────────────
  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie =
        name + "=" + value + ";expires=" + d.toUTCString() + ";path=/;SameSite=Lax";
    } catch (e) {}
  }
  function hasCookie(name) {
    try {
      return document.cookie.split(";").some(function (c) {
        return c.trim().indexOf(name + "=") === 0;
      });
    } catch (e) {
      return false;
    }
  }
  function isSuppressed() {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return true;
    } catch (e) {}
    return hasCookie(COOKIE_NAME);
  }
  function suppress() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {}
    setCookie(COOKIE_NAME, "1", SUPPRESS_DAYS);
  }

  // ── element refs ─────────────────────────────────────────────────────
  var form = root.querySelector("[data-dep-form]");
  var emailInput = form ? form.querySelector('input[name="dep_email"]') : null;
  var submitBtn = root.querySelector("[data-dep-submit]");
  var errorEl = root.querySelector("[data-dep-error]");
  var codeEl = root.querySelector("[data-dep-code]");
  var copyBtn = root.querySelector("[data-dep-copy]");
  var nativeForm = root.querySelector("#dep-native-customer-form");
  var nativeEmail = root.querySelector("[data-dep-native-email]");
  var endpoint = root.getAttribute("data-endpoint");
  var productHandle = root.getAttribute("data-product-handle") || "";
  var productTitle = root.getAttribute("data-product-title") || "";
  var locale = root.getAttribute("data-locale") || "";

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ── open / close ─────────────────────────────────────────────────────
  function open() {
    root.classList.add("dep-open");
    if (emailInput) {
      try { emailInput.focus(); } catch (e) {}
    }
  }
  function close() {
    root.classList.remove("dep-open");
  }
  function dismiss() {
    suppress();
    close();
  }

  root.querySelectorAll("[data-dep-dismiss]").forEach(function (el) {
    el.addEventListener("click", function (e) {
      e.preventDefault();
      dismiss();
    });
  });
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && root.classList.contains("dep-open")) dismiss();
  });

  function setError(msg) {
    if (errorEl) errorEl.textContent = msg || "";
  }

  // ── native Shopify customer form (background submit) ──────────────────
  function submitNativeForm(email) {
    if (!nativeForm) return;
    try {
      if (nativeEmail) nativeEmail.value = email;
      // Route to the hidden iframe so the page does not navigate.
      nativeForm.setAttribute("target", "dep-native-sink");
      nativeForm.submit();
    } catch (e) {
      /* non-fatal — the edge function is the primary path */
    }
  }

  // ── submit handler ───────────────────────────────────────────────────
  function onSubmit(e) {
    e.preventDefault();
    setError("");

    var email = (emailInput && emailInput.value ? emailInput.value : "").trim();
    if (!EMAIL_RE.test(email)) {
      setError("Please enter a valid email.");
      return;
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "Joining…";
    }

    // 2) Fire the native Shopify customer form in the background.
    submitNativeForm(email);

    // 1) POST to the edge function.
    var done = function (code) {
      if (codeEl && code) codeEl.textContent = code;
      suppress();
      root.setAttribute("data-state", "success");
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = "Join the list";
      }
    };

    if (!endpoint) {
      done("SWEAT11");
      return;
    }

    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email,
        source: "theme-popup",
        product_handle: productHandle,
        product_title: productTitle,
        locale: locale,
      }),
    })
      .then(function (r) {
        return r.json().catch(function () {
          return {};
        });
      })
      .then(function (data) {
        done(data && data.code ? data.code : "SWEAT11");
      })
      .catch(function () {
        // Network failure: the native form likely still went through, and the
        // shared code is fixed — show success rather than blocking the user.
        done("SWEAT11");
      });
  }

  if (form) form.addEventListener("submit", onSubmit);

  // ── copy button ──────────────────────────────────────────────────────
  if (copyBtn) {
    copyBtn.addEventListener("click", function () {
      var code = codeEl ? codeEl.textContent.trim() : "SWEAT11";
      var flash = function () {
        var prev = copyBtn.textContent;
        copyBtn.textContent = "Copied";
        setTimeout(function () {
          copyBtn.textContent = prev;
        }, 1600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(flash, flash);
      } else {
        try {
          var ta = document.createElement("textarea");
          ta.value = code;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
          flash();
        } catch (e) {}
      }
    });
  }

  // ── trigger ──────────────────────────────────────────────────────────
  if (!isSuppressed()) {
    var triggered = false;
    var timer;
    var trigger = function () {
      if (triggered) return;
      triggered = true;
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      if (!isSuppressed()) open();
    };
    var onScroll = function () {
      if (window.scrollY > window.innerHeight * SCROLL_TRIGGER_RATIO) trigger();
    };
    timer = setTimeout(trigger, SHOW_DELAY_MS);
    window.addEventListener("scroll", onScroll, { passive: true });
  }
})();
